/**
 * ChannelDiscoveryPage — premium directory of community chat channels.
 *
 * Shows BOTH built-in rooms (General, Ethereum, Signals, Elite Lounge, …)
 * AND user-created channels. Fetches once and filters client-side so
 * category counts stay accurate (the previous server-filter approach
 * showed e.g. General (2) on load then dropped to (0) when clicked).
 *
 * Also surfaces a "My Channels" stats panel — number you've created,
 * total members across your channels, your top and least-popular rooms.
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Search, Hash, Users, Loader2, Plus, Crown, Sparkles, MessageSquare,
  TrendingUp, ChevronRight, X, Trash2, Flame, Award, Shield, Star,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { discoverChannels, joinChannel, leaveChannel, createChatChannel, deleteChannel } from '../services/api';

// Categories the backend actually uses. Reserved ones (official / networks /
// exchanges / exclusive) only apply to built-in rooms; users can't create
// channels with those categories.
const CATEGORIES = [
  { id: 'all',       label: 'All',         icon: '🌐', color: 'from-violet-500/20 to-purple-500/20 border-violet-400/40 text-violet-200',  user: true,  reserved: false },
  { id: 'official',  label: 'Official',    icon: '⭐', color: 'from-amber-500/20 to-orange-500/20 border-amber-400/40 text-amber-200',   user: false, reserved: true  },
  { id: 'networks',  label: 'Networks',    icon: '🌐', color: 'from-blue-500/20 to-cyan-500/20 border-blue-400/40 text-blue-200',        user: false, reserved: true  },
  { id: 'exchanges', label: 'Exchanges',   icon: '🏦', color: 'from-emerald-500/20 to-teal-500/20 border-emerald-400/40 text-emerald-200', user: false, reserved: true },
  { id: 'trading',   label: 'Trading',     icon: '📈', color: 'from-cyan-500/20 to-blue-500/20 border-cyan-400/40 text-cyan-200',        user: true,  reserved: false },
  { id: 'exclusive', label: 'Exclusive',   icon: '👑', color: 'from-violet-500/25 to-fuchsia-500/25 border-violet-400/40 text-violet-200', user: false, reserved: true },
  { id: 'signals',   label: 'Signals',     icon: '🎯', color: 'from-emerald-500/20 to-teal-500/20 border-emerald-400/40 text-emerald-200', user: true,  reserved: false },
  { id: 'analysis',  label: 'Analysis',    icon: '📊', color: 'from-sky-500/20 to-blue-500/20 border-sky-400/40 text-sky-200',           user: true,  reserved: false },
  { id: 'education', label: 'Education',   icon: '🎓', color: 'from-amber-500/20 to-orange-500/20 border-amber-400/40 text-amber-200',   user: true,  reserved: false },
  { id: 'memecoins', label: 'Memecoins',   icon: '🐸', color: 'from-pink-500/20 to-rose-500/20 border-pink-400/40 text-pink-200',        user: true,  reserved: false },
  { id: 'general',   label: 'General',     icon: '💬', color: 'from-slate-500/20 to-gray-500/20 border-slate-400/40 text-slate-200',     user: true,  reserved: false },
];

// Categories selectable in the create modal — only the ones users can pick.
const USER_SELECTABLE_CATEGORIES = CATEGORIES.filter(c => c.user && c.id !== 'all');

const SORTS = [
  { id: 'popular',      label: 'Most Popular' },
  { id: 'newest',       label: 'Newest First' },
  { id: 'alphabetical', label: 'A → Z' },
];

const ICONS = ['💬', '🎯', '💡', '🔥', '💎', '🏆', '📈', '🐂', '🐻', '🎮', '🤖', '🌍', '⚡', '🧠', '🎵', '🐸', '🎓', '📊'];

export default function ChannelDiscoveryPage() {
  const { user } = useAuth();

  // We fetch ALL channels in one shot and filter client-side. This is the
  // single source of truth: category counts and category filtering now
  // agree, and clicking a category is instant (no extra round-trip).
  const [allChannels, setAllChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  // Source filter cuts the list by WHO owns the channel: platform-curated,
  // community-created, or your own. The user-asked-for distinction between
  // "channels I see" and "channels I/others created."
  const [source, setSource] = useState('all'); // all | platform | community | mine
  const [sortBy, setSortBy] = useState('popular');
  const [toast, setToast] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', description: '', icon: '💬', category: 'general' });
  const [creating, setCreating] = useState(false);

  // Mount-only loader visibility (max 5s)
  const [showLoader, setShowLoader] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setShowLoader(false), 5000);
    return () => clearTimeout(t);
  }, []);

  // Race-safe fetch
  const latestFetchRef = useRef(0);

  function reloadChannels() {
    const fetchId = ++latestFetchRef.current;
    const isCurrent = () => fetchId === latestFetchRef.current;
    setLoading(true);
    // Pass empty category — fetch ALL channels, filter on the client.
    return discoverChannels('', '')
      .then(d => {
        if (!isCurrent()) return;
        setAllChannels(d?.channels || []);
      })
      .catch(() => {})
      .finally(() => { if (isCurrent()) setLoading(false); });
  }

  useEffect(() => { reloadChannels(); }, []);

  function showToast(msg, ms = 3000) {
    setToast(msg);
    setTimeout(() => setToast(null), ms);
  }

  async function handleJoin(channelId) {
    try {
      await joinChannel(channelId);
      setAllChannels(prev => prev.map(ch => ch.id === channelId ? { ...ch, joined: true, memberCount: (ch.memberCount || 0) + 1 } : ch));
      showToast('Joined channel!');
    } catch (err) {
      showToast(err.message || 'Failed to join');
    }
  }

  async function handleLeave(channelId) {
    try {
      await leaveChannel(channelId);
      setAllChannels(prev => prev.map(ch => ch.id === channelId ? { ...ch, joined: false, memberCount: Math.max(0, (ch.memberCount || 1) - 1) } : ch));
      showToast('Left channel');
    } catch {}
  }

  async function handleDelete(channelId, name) {
    if (!confirm(`Delete channel "${name}"? This cannot be undone.`)) return;
    try {
      await deleteChannel(channelId);
      setAllChannels(prev => prev.filter(c => c.id !== channelId));
      showToast('Channel deleted');
    } catch {
      showToast('Failed to delete');
    }
  }

  async function handleCreate() {
    if (!createForm.name.trim()) return;
    setCreating(true);
    try {
      await createChatChannel(createForm.name, createForm.description, createForm.icon, createForm.category);
      setShowCreate(false);
      setCreateForm({ name: '', description: '', icon: '💬', category: 'general' });
      showToast('Channel created!');
      await reloadChannels();
    } catch (err) {
      showToast(err.message || 'Failed to create');
    }
    setCreating(false);
  }

  // ─── Ownership check (robust) ───────────────────────────────────────
  // Channels store `createdBy` (userId) and `createdByName` (email prefix)
  // at creation time. Those values can drift from the current session's
  // user.userId — JWT defaults to 'admin' when no explicit userId, the
  // server may issue different ids across logins, etc. So we match on
  // ANY of: same userId / same email-prefix / admin-on-admin-created.
  const userEmailPrefix = (user?.email || '').split('@')[0].toLowerCase();
  const isMyChannel = (ch) => {
    if (!ch || ch.isBuiltIn) return false;
    if (ch.createdBy && user?.userId && ch.createdBy === user.userId) return true;
    if (ch.createdByName && userEmailPrefix &&
        ch.createdByName.toLowerCase() === userEmailPrefix) return true;
    if (user?.role === 'admin' && ch.createdBy === 'admin') return true;
    return false;
  };

  // ─── Source-filtered base list ──────────────────────────────────────
  // Apply the Source filter first so category counts reflect the chosen
  // source. E.g. switching to "Created by me" makes the category pills
  // count only your channels.
  const sourceFiltered = useMemo(() => {
    if (source === 'platform')  return allChannels.filter(c => c.isBuiltIn);
    if (source === 'community') return allChannels.filter(c => !c.isBuiltIn && !isMyChannel(c));
    if (source === 'mine')      return allChannels.filter(isMyChannel);
    return allChannels;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allChannels, source, user]);

  // ─── Filtered / sorted view ────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    let list = sourceFiltered;
    if (category !== 'all') list = list.filter(c => (c.category || 'general') === category);
    if (q) {
      list = list.filter(c =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.description || '').toLowerCase().includes(q)
      );
    }
    list = [...list];
    if (sortBy === 'popular') {
      list.sort((a, b) => (b.memberCount || 0) - (a.memberCount || 0));
    } else if (sortBy === 'newest') {
      list.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    } else if (sortBy === 'alphabetical') {
      list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }
    return list;
  }, [sourceFiltered, category, search, sortBy]);

  // Source counts — total per source (independent of category)
  const sourceCounts = useMemo(() => ({
    all:       allChannels.length,
    platform:  allChannels.filter(c => c.isBuiltIn).length,
    community: allChannels.filter(c => !c.isBuiltIn && !isMyChannel(c)).length,
    mine:      allChannels.filter(isMyChannel).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [allChannels, user]);

  // Category counts respect the chosen source filter, so the numbers
  // are always honest for the slice you're looking at.
  const categoryCounts = useMemo(() => {
    const counts = { all: sourceFiltered.length };
    CATEGORIES.forEach(c => {
      if (c.id !== 'all') counts[c.id] = sourceFiltered.filter(ch => (ch.category || 'general') === c.id).length;
    });
    return counts;
  }, [sourceFiltered]);

  // ─── Top-level KPIs ─────────────────────────────────────────────────
  const totalChannels = allChannels.length;
  const totalMembers = useMemo(() => allChannels.reduce((sum, c) => sum + (c.memberCount || 0), 0), [allChannels]);
  const myJoinedCount = useMemo(() => allChannels.filter(c => c.joined).length, [allChannels]);
  const hotChannels = useMemo(() => allChannels.filter(c => (c.memberCount || 0) >= 10).length, [allChannels]);

  // ─── My Channels stats (only ones THIS user created) ──────────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const myCreated = useMemo(() => allChannels.filter(isMyChannel), [allChannels, user]);
  const myStats = useMemo(() => {
    if (myCreated.length === 0) return null;
    const sorted = [...myCreated].sort((a, b) => (b.memberCount || 0) - (a.memberCount || 0));
    return {
      count: myCreated.length,
      totalMembers: myCreated.reduce((sum, c) => sum + (c.memberCount || 0), 0),
      topChannel: sorted[0],
      bottomChannel: sorted[sorted.length - 1],
    };
  }, [myCreated]);

  // Spotlight: top 3 by member count, only when no filter and on popular sort
  const showSpotlight = category === 'all' && !search.trim() && sortBy === 'popular';
  const spotlight = showSpotlight ? filtered.slice(0, 3) : [];
  const restAfterSpotlight = showSpotlight ? filtered.slice(3) : filtered;

  return (
    <div className="space-y-6 animate-fade-in max-w-6xl">
      {/* ─── Premium Hero ──────────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-3xl border border-dark-700/50 bg-gradient-to-br from-violet-500/10 via-dark-800/80 to-dark-900 p-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-2xl shadow-violet-500/30">
              <Hash className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                Discover Channels
                <Sparkles className="w-5 h-5 text-amber-400" />
              </h1>
              <p className="text-sm text-gray-400 mt-0.5 max-w-2xl">
                Browse the official rooms, find topic-based community spaces, or spin up your own.
              </p>
            </div>
          </div>
          <button onClick={() => setShowCreate(true)}
            className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-400 hover:to-purple-500 text-white text-sm font-bold flex items-center gap-2 shadow-lg shadow-violet-500/30 transition-all hover:-translate-y-0.5">
            <Plus className="w-4 h-4" /> Create Channel
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          <KPI icon={Hash}           label="Total Channels"  value={totalChannels}  color="violet" />
          <KPI icon={Users}          label="Total Members"   value={totalMembers}   color="cyan" />
          <KPI icon={MessageSquare}  label="You're In"       value={myJoinedCount}  color="emerald" />
          <KPI icon={Flame}          label="Hot Channels"    value={hotChannels}    color="amber" />
        </div>
      </div>

      {/* ─── My Channels stats — only when the user has created any ─── */}
      {myStats && (
        <div className="bg-gradient-to-br from-amber-500/8 via-dark-800/80 to-dark-900 rounded-2xl border border-amber-500/20 p-5">
          <div className="flex items-center gap-2 mb-4">
            <Award className="w-4 h-4 text-amber-400" />
            <h3 className="text-xs font-bold text-amber-300 uppercase tracking-wider">My Channels</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-dark-700/40 rounded-xl p-3 border border-dark-700/50">
              <p className="text-[10px] text-gray-500 uppercase">Created</p>
              <p className="text-2xl font-bold text-amber-300">{myStats.count}</p>
            </div>
            <div className="bg-dark-700/40 rounded-xl p-3 border border-dark-700/50">
              <p className="text-[10px] text-gray-500 uppercase">Total Members</p>
              <p className="text-2xl font-bold text-white">{myStats.totalMembers.toLocaleString()}</p>
            </div>
            {myStats.topChannel && (
              <div className="bg-dark-700/40 rounded-xl p-3 border border-dark-700/50">
                <p className="text-[10px] text-gray-500 uppercase flex items-center gap-1">
                  <TrendingUp className="w-2.5 h-2.5" /> Top
                </p>
                <p className="text-sm font-bold text-emerald-300 truncate flex items-center gap-1">
                  <span>{myStats.topChannel.icon || '💬'}</span> {myStats.topChannel.name}
                </p>
                <p className="text-[10px] text-gray-600">{myStats.topChannel.memberCount || 0} members</p>
              </div>
            )}
            {myStats.bottomChannel && myStats.count > 1 && (
              <div className="bg-dark-700/40 rounded-xl p-3 border border-dark-700/50">
                <p className="text-[10px] text-gray-500 uppercase">Quietest</p>
                <p className="text-sm font-bold text-gray-300 truncate flex items-center gap-1">
                  <span>{myStats.bottomChannel.icon || '💬'}</span> {myStats.bottomChannel.name}
                </p>
                <p className="text-[10px] text-gray-600">{myStats.bottomChannel.memberCount || 0} members</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── Paid-channel promo (LIVE) ───────────────────────────────── */}
      <div className="bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-violet-500/10 rounded-2xl border border-amber-400/20 p-5 flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-amber-500/20">
          <Crown className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="text-white font-bold text-sm flex items-center gap-2">
            Monetize your channel with USDC subscriptions
            <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">LIVE</span>
          </h3>
          <p className="text-gray-400 text-xs mt-0.5">Verified creators can charge a monthly fee. 80% to you, 20% platform — paid out in USDC the moment a subscriber pays.</p>
        </div>
        <a href="/community" className="text-[10px] font-bold text-amber-300 hover:text-amber-200 whitespace-nowrap flex items-center gap-1">
          Learn more <ChevronRight className="w-3 h-3" />
        </a>
      </div>

      {/* ─── Search + sort ─────────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search channels by name or topic..."
            className="w-full bg-dark-800 border border-dark-700/50 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white placeholder-gray-600 focus:border-violet-400 focus:outline-none" />
        </div>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)}
          className="bg-dark-800 border border-dark-700/50 rounded-xl px-4 py-2.5 text-sm text-white focus:border-violet-400 focus:outline-none">
          {SORTS.map(s => <option key={s.id} value={s.id}>Sort: {s.label}</option>)}
        </select>
      </div>

      {/* ─── Source tabs (who created the channel) ──────────────────── */}
      <div>
        <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2 font-semibold">Source</p>
        <div className="flex gap-2 overflow-x-auto pb-1">
          <SourceTab id="all"       label="All Sources"     icon="🌐" active={source === 'all'}       count={sourceCounts.all}       color="violet"  onClick={setSource} />
          <SourceTab id="platform"  label="Platform"        icon="⭐" active={source === 'platform'}  count={sourceCounts.platform}  color="cyan"    onClick={setSource} />
          <SourceTab id="community" label="Community"       icon="👥" active={source === 'community'} count={sourceCounts.community} color="emerald" onClick={setSource} />
          <SourceTab id="mine"      label="Created by Me"   icon="✨" active={source === 'mine'}      count={sourceCounts.mine}      color="amber"   onClick={setSource} />
        </div>
      </div>

      {/* ─── Category pills ────────────────────────────────────────── */}
      <div>
        <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2 font-semibold">Category</p>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {CATEGORIES.map(c => {
            const isActive = category === c.id;
            const count = categoryCounts[c.id] || 0;
            // Dim categories that have 0 channels for the current source
            const isEmpty = c.id !== 'all' && count === 0;
            return (
              <button key={c.id} onClick={() => setCategory(c.id)}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-2xl text-xs font-semibold whitespace-nowrap transition-all border ${
                  isActive
                    ? `bg-gradient-to-br ${c.color}`
                    : isEmpty
                      ? 'bg-dark-800/50 text-gray-600 border-dark-700/30 cursor-default'
                      : 'bg-dark-800 text-gray-400 hover:text-white border-dark-700/50 hover:border-dark-600'
                }`}>
                <span>{c.icon}</span> {c.label}
                <span className={`text-[10px] px-1.5 py-px rounded ${isActive ? 'bg-white/10' : 'bg-dark-700 text-gray-500'}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ─── Spotlight (top 3 most popular) ──────────────────────────── */}
      {spotlight.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-xs font-bold text-violet-300 uppercase tracking-wider flex items-center gap-2">
            <Flame className="w-3.5 h-3.5" /> Most Popular Channels
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {spotlight.map(ch => (
              <SpotlightChannelCard key={ch.id} channel={ch} onJoin={() => handleJoin(ch.id)} onLeave={() => handleLeave(ch.id)} />
            ))}
          </div>
        </div>
      )}

      {/* ─── Channel grid ───────────────────────────────────────────── */}
      {/* Empty-state check uses `filtered` (not restAfterSpotlight) — if a
          source/category has only 1-3 channels they all live in Spotlight
          and the rest is empty, which is NOT the same as "no channels". */}
      {loading && showLoader && allChannels.length === 0 ? (
        <div className="bg-dark-800/40 rounded-2xl border border-dark-700/50 p-8 flex items-center justify-center gap-3">
          <Loader2 className="w-5 h-5 text-violet-400 animate-spin" />
          <span className="text-sm text-gray-400">Loading channels…</span>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-dark-800 rounded-2xl p-12 border border-dark-700/50 text-center">
          <Hash className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-white font-bold mb-2">
            {search.trim() || category !== 'all' || source !== 'all'
              ? 'No matching channels'
              : 'No channels yet'}
          </h3>
          <p className="text-gray-500 text-sm mb-4">
            {search.trim() || category !== 'all' || source !== 'all'
              ? 'Try a different search, category, or source.'
              : 'Be the first to create one!'}
          </p>
          {!search.trim() && category === 'all' && source === 'all' && (
            <button onClick={() => setShowCreate(true)}
              className="px-4 py-2 rounded-xl bg-violet-500 hover:bg-violet-400 text-white text-sm font-bold inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Create Channel
            </button>
          )}
        </div>
      ) : restAfterSpotlight.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {restAfterSpotlight.map(ch => (
            <ChannelCard key={ch.id} channel={ch}
              isOwnerOrAdmin={isMyChannel(ch) || user?.role === 'admin'}
              isMine={isMyChannel(ch)}
              onJoin={() => handleJoin(ch.id)}
              onLeave={() => handleLeave(ch.id)}
              onDelete={() => handleDelete(ch.id, ch.name)} />
          ))}
        </div>
      ) : null}

      {/* ─── Create modal ─────────────────────────────────────────────── */}
      {showCreate && (
        <CreateModal
          form={createForm} setForm={setCreateForm}
          creating={creating} onClose={() => setShowCreate(false)}
          onSubmit={handleCreate} />
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl shadow-2xl text-sm font-medium bg-dark-700 text-white border border-dark-600">
          {toast}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Sub-components
// ════════════════════════════════════════════════════════════════════════════

function SourceTab({ id, label, icon, active, count, color, onClick }) {
  const colors = {
    violet:  active ? 'bg-violet-500/15 text-violet-200 border-violet-500/40'   : '',
    cyan:    active ? 'bg-cyan-500/15 text-cyan-200 border-cyan-500/40'         : '',
    emerald: active ? 'bg-emerald-500/15 text-emerald-200 border-emerald-500/40' : '',
    amber:   active ? 'bg-amber-500/15 text-amber-200 border-amber-500/40'      : '',
  };
  return (
    <button onClick={() => onClick(id)}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-bold whitespace-nowrap transition-all border ${
        active
          ? colors[color]
          : 'bg-dark-800 text-gray-400 hover:text-white border-dark-700/50 hover:border-dark-600'
      }`}>
      <span>{icon}</span>
      {label}
      <span className={`text-[10px] px-1.5 py-px rounded ${active ? 'bg-white/10' : 'bg-dark-700 text-gray-500'}`}>
        {count}
      </span>
    </button>
  );
}

function KPI({ icon: Icon, label, value, color }) {
  const colors = {
    violet:  'from-violet-500/10 to-purple-500/10 border-violet-500/20 text-violet-300',
    cyan:    'from-cyan-500/10 to-blue-500/10 border-cyan-500/20 text-cyan-300',
    emerald: 'from-emerald-500/10 to-teal-500/10 border-emerald-500/20 text-emerald-300',
    amber:   'from-amber-500/10 to-orange-500/10 border-amber-500/20 text-amber-300',
  };
  return (
    <div className={`bg-gradient-to-br ${colors[color]} rounded-xl p-3 border`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5" />
        <p className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</p>
      </div>
      <p className="text-2xl font-bold text-white">{typeof value === 'number' ? value.toLocaleString() : value}</p>
    </div>
  );
}

function SpotlightChannelCard({ channel, onJoin, onLeave }) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-violet-500/30 transition-all hover:-translate-y-1 hover:shadow-2xl hover:shadow-violet-500/10"
      style={{ background: 'linear-gradient(160deg, rgba(139,92,246,0.10) 0%, rgba(16,16,20,1) 50%)' }}>
      <div className="h-1 bg-gradient-to-r from-violet-400 via-purple-400 to-violet-400" />
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500/20 to-purple-500/20 ring-2 ring-violet-400/30 flex items-center justify-center text-3xl flex-shrink-0">
            {channel.icon || '💬'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <h3 className="text-sm font-bold text-white truncate">{channel.name}</h3>
              {channel.isBuiltIn && (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30 inline-flex items-center gap-0.5">
                  <Star className="w-2.5 h-2.5" /> OFFICIAL
                </span>
              )}
              {!channel.isBuiltIn && (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30 inline-flex items-center gap-0.5">
                  <Flame className="w-2.5 h-2.5" /> HOT
                </span>
              )}
            </div>
            <p className="text-[10px] text-gray-500 mt-0.5">
              <Users className="w-2.5 h-2.5 inline mr-0.5" />
              {(channel.memberCount || 0).toLocaleString()} members
            </p>
          </div>
        </div>
        <p className="text-[11px] text-gray-400 mt-3 leading-relaxed line-clamp-2 min-h-[2.4rem]">
          {channel.description || 'Community channel'}
        </p>
        <div className="mt-3 pt-3 border-t border-violet-500/15 flex items-center gap-2">
          {channel.joined ? (
            <>
              <a href="/community" className="flex-1 py-2 rounded-lg text-xs font-bold bg-violet-500/15 text-violet-200 text-center hover:bg-violet-500/25 transition-all border border-violet-500/30">
                Open Chat
              </a>
              <button onClick={onLeave}
                className="px-3 py-2 rounded-lg text-xs text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition-all">
                Leave
              </button>
            </>
          ) : (
            <button onClick={onJoin}
              className="flex-1 py-2 rounded-lg text-xs font-bold bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-400 hover:to-purple-500 text-white transition-all">
              Join Channel
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ChannelCard({ channel, isOwnerOrAdmin, isMine, onJoin, onLeave, onDelete }) {
  const isHot = (channel.memberCount || 0) >= 10;
  return (
    <div className={`bg-dark-800 rounded-2xl border transition-all overflow-hidden ${
      channel.joined
        ? 'border-violet-500/30 shadow-lg shadow-violet-500/5'
        : isMine
          ? 'border-amber-500/30'
          : 'border-dark-700/50 hover:border-dark-600'
    }`}>
      <div className={`h-0.5 ${
        channel.joined
          ? 'bg-gradient-to-r from-violet-400/40 via-violet-400/70 to-violet-400/40'
          : isMine
            ? 'bg-gradient-to-r from-amber-400/40 via-amber-400/70 to-amber-400/40'
            : channel.isBuiltIn
              ? 'bg-gradient-to-r from-cyan-400/30 via-cyan-400/50 to-cyan-400/30'
              : 'bg-gradient-to-r from-accent-primary/30 via-accent-primary/50 to-accent-primary/30'
      }`} />
      <div className="p-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-dark-700 flex items-center justify-center text-2xl flex-shrink-0">
            {channel.icon || '💬'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <h3 className="text-sm font-bold text-white truncate">{channel.name}</h3>
              {channel.joined && (
                <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300 border border-violet-500/30">JOINED</span>
              )}
              {isMine && !channel.joined && (
                <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">YOURS</span>
              )}
              {channel.isBuiltIn && !channel.joined && !isMine && (
                <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 inline-flex items-center gap-0.5">
                  <Star className="w-2.5 h-2.5" />
                </span>
              )}
              {isHot && !channel.joined && !isMine && !channel.isBuiltIn && (
                <span className="text-[8px] font-bold px-1 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 inline-flex items-center gap-0.5">
                  <Flame className="w-2.5 h-2.5" />
                </span>
              )}
            </div>
            <p className="text-[10px] text-gray-500 truncate">{channel.description || 'Community channel'}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <span className="text-[10px] text-gray-500 inline-flex items-center gap-1">
            <Users className="w-3 h-3" /> {(channel.memberCount || 0).toLocaleString()}
          </span>

          {/* Source attribution — be explicit so users can tell platform
              vs community at a glance. */}
          {channel.isBuiltIn ? (
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-cyan-500/10 text-cyan-300 border border-cyan-500/20 inline-flex items-center gap-0.5">
              <Star className="w-2.5 h-2.5" /> PLATFORM
            </span>
          ) : isMine ? (
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/20">
              by you
            </span>
          ) : channel.createdByName && channel.createdBy !== 'system' ? (
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 truncate max-w-[120px]">
              by {channel.createdByName}
            </span>
          ) : null}

          {channel.category && (
            <span className="ml-auto text-[9px] uppercase tracking-wider text-gray-600">{channel.category}</span>
          )}
        </div>

        <div className="mt-3 pt-3 border-t border-dark-700/50 flex items-center gap-2">
          {channel.joined ? (
            <>
              <a href="/community" className="flex-1 py-2 rounded-lg text-xs font-bold bg-violet-500/10 text-violet-300 text-center hover:bg-violet-500/20 transition-all border border-violet-500/20">
                Open Chat
              </a>
              <button onClick={onLeave}
                className="px-3 py-2 rounded-lg text-xs text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition-all">
                Leave
              </button>
            </>
          ) : (
            <button onClick={onJoin}
              className="flex-1 py-2 rounded-lg text-xs font-bold bg-violet-500 hover:bg-violet-400 text-white transition-all">
              Join Channel
            </button>
          )}
          {isOwnerOrAdmin && !channel.isBuiltIn && (
            <button onClick={onDelete}
              className="px-2 py-2 rounded-lg text-[10px] text-gray-600 hover:text-red-400 transition-all"
              title="Delete channel">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CreateModal({ form, setForm, creating, onClose, onSubmit }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-dark-800 rounded-2xl border border-violet-500/20 shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="h-1 bg-gradient-to-r from-violet-400 via-purple-400 to-violet-400" />
        <div className="p-5 border-b border-dark-700/50 flex items-center justify-between"
          style={{ background: 'linear-gradient(180deg, rgba(139,92,246,0.06) 0%, transparent 100%)' }}>
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Hash className="w-5 h-5 text-violet-400" /> Create Channel
          </h2>
          <button onClick={onClose} className="p-1.5 text-gray-500 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-xs text-gray-500 mb-2">Channel Icon</label>
            <div className="flex flex-wrap gap-1.5">
              {ICONS.map(icon => (
                <button key={icon} onClick={() => setForm(f => ({ ...f, icon }))}
                  className={`w-8 h-8 rounded-lg flex items-center justify-center text-lg transition-all ${
                    form.icon === icon
                      ? 'bg-violet-500/20 ring-1 ring-violet-400'
                      : 'bg-dark-700 hover:bg-dark-600'
                  }`}>{icon}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Channel Name *</label>
            <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Solana Alpha" maxLength={30}
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-600 focus:border-violet-400 focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Description</label>
            <input type="text" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="What's this channel about?" maxLength={100}
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-600 focus:border-violet-400 focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-2">Category</label>
            <div className="grid grid-cols-3 gap-2">
              {USER_SELECTABLE_CATEGORIES.map(c => {
                const isActive = form.category === c.id;
                return (
                  <button key={c.id} onClick={() => setForm(f => ({ ...f, category: c.id }))}
                    className={`flex items-center justify-center gap-1.5 px-2 py-2 rounded-xl text-xs font-semibold transition-all border ${
                      isActive
                        ? `bg-gradient-to-br ${c.color}`
                        : 'bg-dark-700 text-gray-400 hover:text-white border-dark-600 hover:border-dark-500'
                    }`}>
                    <span>{c.icon}</span> {c.label}
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-gray-600 mt-1.5">Choose where your channel appears in Discover.</p>
            <p className="text-[10px] text-gray-600 mt-1">
              <span className="text-cyan-400/70">Official · Networks · Exchanges · Exclusive</span> are reserved for platform-curated rooms and can't be chosen here.
            </p>
          </div>
          <button onClick={onSubmit} disabled={creating || !form.name.trim()}
            className="w-full py-3 rounded-xl text-sm font-bold bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-400 hover:to-purple-500 text-white disabled:opacity-40 transition-all flex items-center justify-center gap-2 shadow-lg shadow-violet-500/20">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Create Channel
          </button>
        </div>
      </div>
    </div>
  );
}
